import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
    Home, FileText, CreditCard, LineChart, UserCircle2,
    ChevronLeft, Loader2, Mail, X, Plus, Users, Calendar, Map, Check,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const PRIMARY = "#2D5BFF";
const PRIMARY_DARK = "#1B3DBF";
const GRADIENT = `linear-gradient(135deg, ${PRIMARY} 0%, ${PRIMARY_DARK} 100%)`;

const NAV_ITEMS = [
    { icon: Home, label: "Home", key: "home" },
    { icon: FileText, label: "Receipts", key: "receipts" },
    { icon: CreditCard, label: "Cards", key: "cards" },
    { icon: LineChart, label: "Insights", key: "insights" },
    { icon: UserCircle2, label: "Profile", key: "profile" },
];

const GROUP_TYPES = [
    {
        value: "household",
        label: "Household",
        description: "Share the family shop, bills & cards",
        icon: Home,
        bg: "#16A34A",
    },
    {
        value: "trip",
        label: "Trip",
        description: "Track spending on a holiday or trip",
        icon: Map,
        bg: "#7C3AED",
    },
    {
        value: "event",
        label: "Event",
        description: "A wedding, party or one-off occasion",
        icon: Calendar,
        bg: "#EA580C",
    },
    {
        value: "community",
        label: "Community",
        description: "A club, team or shared interest group",
        icon: Users,
        bg: "#0D9488",
    },
    {
        value: "custom",
        label: "Custom",
        description: "Name it whatever you like",
        icon: Plus,
        bg: "#4338CA",
    },
];

const NAME_PLACEHOLDERS = {
    household: "Household name",
    trip: "Trip name",
    event: "Event name",
    community: "Community name",
    custom: "Circle name",
};

export default function CreateGroupPage() {
    const { user: authUser } = useAuth();
    const navigate = useNavigate();
    const [isMobile, setIsMobile] = useState(false);
    const [selectedType, setSelectedType] = useState("household");
    const [name, setName] = useState("");
    const [inviteEmail, setInviteEmail] = useState("");
    const [invitedEmails, setInvitedEmails] = useState([]);
    const [creating, setCreating] = useState(false);
    const [errors, setErrors] = useState({});

    useEffect(() => {
        const check = () => setIsMobile(window.innerWidth < 768);
        check();
        window.addEventListener("resize", check);
        return () => window.removeEventListener("resize", check);
    }, []);

    const onNavClick = (key) => {
        if (key === "home") navigate("/dashboard");
        else if (key === "receipts") navigate("/receipts");
        else if (key === "cards") navigate("/cards");
        else if (key === "insights") navigate("/insights");
        else if (key === "profile") navigate("/profile");
    };

    const addEmail = () => {
        const email = inviteEmail.trim().toLowerCase();
        if (!email) return;
        if (!email.includes("@") || !email.includes(".")) {
            setErrors(prev => ({ ...prev, email: "Enter a valid email address" }));
            return;
        }
        if (invitedEmails.includes(email)) {
            setErrors(prev => ({ ...prev, email: "Already added" }));
            return;
        }
        setInvitedEmails(prev => [...prev, email]);
        setInviteEmail("");
        setErrors(prev => ({ ...prev, email: null }));
    };

    const removeEmail = (email) => {
        setInvitedEmails(prev => prev.filter(e => e !== email));
    };

    const handleCreate = async () => {
        const trimmedName = name.trim();
        if (!trimmedName) {
            setErrors({ name: "Enter a circle name" });
            return;
        }
        setErrors({});
        setCreating(true);

        // Include any email still typed in the input (user didn't click "Add")
        const pendingEmail = inviteEmail.trim().toLowerCase();
        const allEmails = [...invitedEmails];
        if (pendingEmail && pendingEmail.includes("@") && pendingEmail.includes(".") && !allEmails.includes(pendingEmail)) {
            allEmails.push(pendingEmail);
        }

        try {
            // 1. Create the group
            const res = await axios.post(`${API}/groups`, {
                name: trimmedName,
                type: selectedType,
            }, { withCredentials: true });

            if (!res.data.success) {
                toast.error(res.data.error || "Couldn't create group");
                setCreating(false);
                return;
            }

            const groupId = res.data.group.id;

            // 2. Send invites
            let invitesSent = 0;
            for (const email of allEmails) {
                try {
                    await axios.post(`${API}/groups/${groupId}/invite`, {
                        email,
                        role: "contributor",
                    }, { withCredentials: true });
                    invitesSent++;
                } catch (err) {
                    const detail = err.response?.data?.detail;
                    console.warn(`Invite to ${email} failed:`, detail || err.message);
                    toast.error(typeof detail === "string" ? detail : `Couldn't invite ${email}`);
                }
            }

            if (invitesSent > 0) {
                toast.success(`Group created! ${invitesSent} invite${invitesSent > 1 ? 's' : ''} sent`);
            } else {
                toast.success("Group created!");
            }
            navigate(`/groups/${groupId}`, { replace: true });
        } catch (err) {
            const detail = err.response?.data?.detail;
            if (typeof detail === "object" && detail.validation_errors) {
                setErrors(detail.validation_errors);
            } else {
                toast.error(typeof detail === "string" ? detail : "Couldn't create group");
            }
        } finally {
            setCreating(false);
        }
    };

    // ── Shared form content ──────────────────────────────────────────────────
    const formContent = () => (
        <div>
            {/* TYPE */}
            <p style={{ color: PRIMARY, fontSize: 12, fontWeight: 700, fontFamily: "DM Sans, sans-serif", letterSpacing: 1, marginBottom: 12 }}>
                TYPE
            </p>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                {GROUP_TYPES.map((t) => {
                    const isSelected = selectedType === t.value;
                    const Icon = t.icon;
                    return (
                        <button
                            key={t.value}
                            onClick={() => setSelectedType(t.value)}
                            style={{
                                padding: "16px 14px",
                                borderRadius: 16,
                                border: isSelected ? `2px solid ${PRIMARY}` : "2px solid #E5E7EB",
                                backgroundColor: isSelected ? "#F0F4FF" : "#FFFFFF",
                                textAlign: "left",
                                transition: "all 0.15s",
                            }}
                        >
                            <div
                                className="flex items-center justify-center"
                                style={{
                                    width: 40, height: 40, borderRadius: 12,
                                    backgroundColor: isSelected ? t.bg : "#F3F4F6",
                                    marginBottom: 10,
                                }}
                            >
                                <Icon className="w-5 h-5" style={{ color: isSelected ? "#FFFFFF" : "#6B7280" }} />
                            </div>
                            <p style={{ color: "#111827", fontSize: 14, fontWeight: 700, fontFamily: "Manrope, sans-serif" }}>
                                {t.label}
                            </p>
                            <p style={{ color: "#9CA3AF", fontSize: 12, fontFamily: "DM Sans, sans-serif", marginTop: 2, lineHeight: 1.4 }}>
                                {t.description}
                            </p>
                        </button>
                    );
                })}
            </div>

            {/* NAME */}
            <p style={{ color: PRIMARY, fontSize: 12, fontWeight: 700, fontFamily: "DM Sans, sans-serif", letterSpacing: 1, marginTop: 28, marginBottom: 12 }}>
                NAME
            </p>
            <input
                type="text"
                value={name}
                onChange={(e) => { setName(e.target.value); setErrors(prev => ({ ...prev, name: null })); }}
                placeholder={NAME_PLACEHOLDERS[selectedType] || "Circle name"}
                className="w-full"
                style={{
                    height: 52, paddingLeft: 16, paddingRight: 16,
                    borderRadius: 14, border: errors.name ? "1.5px solid #EF4444" : "1.5px solid #E5E7EB",
                    backgroundColor: "#FAFAFA", fontSize: 16,
                    fontFamily: "DM Sans, sans-serif", color: "#111827",
                    outline: "none",
                }}
            />
            {errors.name && (
                <p style={{ color: "#EF4444", fontSize: 12, fontFamily: "DM Sans, sans-serif", marginTop: 6 }}>
                    {errors.name}
                </p>
            )}

            {/* INVITE PEOPLE */}
            <div className="flex items-baseline gap-2" style={{ marginTop: 28, marginBottom: 4 }}>
                <p style={{ color: PRIMARY, fontSize: 12, fontWeight: 700, fontFamily: "DM Sans, sans-serif", letterSpacing: 1 }}>
                    INVITE PEOPLE
                </p>
                <p style={{ color: "#9CA3AF", fontSize: 12, fontFamily: "DM Sans, sans-serif" }}>
                    · optional
                </p>
            </div>
            <p style={{ color: "#6B7280", fontSize: 13, fontFamily: "DM Sans, sans-serif", marginBottom: 12, lineHeight: 1.5 }}>
                Members join by email. They'll need a Google account to join.
            </p>

            <div className="flex items-center gap-2">
                <div className="flex-1 flex items-center" style={{
                    height: 48, borderRadius: 14, border: errors.email ? "1.5px solid #EF4444" : "1.5px solid #E5E7EB",
                    backgroundColor: "#FAFAFA", paddingLeft: 12, paddingRight: 8,
                }}>
                    <Mail className="w-4 h-4 flex-shrink-0" style={{ color: "#9CA3AF", marginRight: 8 }} />
                    <input
                        type="email"
                        value={inviteEmail}
                        onChange={(e) => { setInviteEmail(e.target.value); setErrors(prev => ({ ...prev, email: null })); }}
                        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addEmail(); } }}
                        placeholder="name@email.com"
                        className="flex-1 bg-transparent"
                        style={{ fontSize: 16, fontFamily: "DM Sans, sans-serif", color: "#111827", outline: "none", border: "none" }}
                    />
                </div>
                <button
                    onClick={addEmail}
                    style={{
                        height: 48, paddingLeft: 20, paddingRight: 20,
                        borderRadius: 14, border: `1.5px solid ${PRIMARY}`,
                        color: PRIMARY, fontSize: 14, fontWeight: 600,
                        fontFamily: "DM Sans, sans-serif", backgroundColor: "#FFFFFF",
                    }}
                >
                    Add
                </button>
            </div>
            {errors.email && (
                <p style={{ color: "#EF4444", fontSize: 12, fontFamily: "DM Sans, sans-serif", marginTop: 6 }}>
                    {errors.email}
                </p>
            )}

            {/* Email chips */}
            {invitedEmails.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-3">
                    {invitedEmails.map((email) => (
                        <div
                            key={email}
                            className="flex items-center gap-1.5"
                            style={{
                                padding: "6px 10px 6px 12px",
                                borderRadius: 999, backgroundColor: "#EEF2FF",
                                fontSize: 13, fontFamily: "DM Sans, sans-serif", color: PRIMARY,
                            }}
                        >
                            <span>{email}</span>
                            <button
                                onClick={() => removeEmail(email)}
                                className="flex items-center justify-center"
                                style={{ width: 18, height: 18, borderRadius: 999, backgroundColor: "rgba(45,91,255,0.15)" }}
                            >
                                <X className="w-3 h-3" style={{ color: PRIMARY }} />
                            </button>
                        </div>
                    ))}
                </div>
            )}

            {/* Create button */}
            <button
                onClick={handleCreate}
                disabled={creating}
                className="w-full flex items-center justify-center gap-2"
                style={{
                    height: 54, borderRadius: 16, marginTop: 32,
                    background: GRADIENT, color: "#FFFFFF",
                    fontSize: 16, fontWeight: 700, fontFamily: "DM Sans, sans-serif",
                    opacity: creating ? 0.7 : 1,
                    boxShadow: "0 8px 20px rgba(45,91,255,0.28)",
                }}
            >
                {creating ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                    <Users className="w-5 h-5" />
                )}
                {creating ? "Creating…" : "Create circle"}
            </button>
        </div>
    );

    // ── MOBILE LAYOUT ────────────────────────────────────────────────────────
    if (isMobile) {
        return (
            <div className="min-h-screen flex flex-col" style={{ backgroundColor: "#F9FAFB" }}>
                <header style={{ background: GRADIENT, padding: "16px 20px 24px 20px", borderBottomLeftRadius: 28, borderBottomRightRadius: 28 }}>
                    <div className="flex items-center gap-3">
                        <button
                            onClick={() => navigate(-1)}
                            className="flex items-center justify-center"
                            style={{ width: 36, height: 36, borderRadius: 999, backgroundColor: "rgba(255,255,255,0.15)" }}
                        >
                            <ChevronLeft className="w-5 h-5" style={{ color: "#FFFFFF" }} />
                        </button>
                        <h1 style={{ color: "#FFFFFF", fontSize: 20, fontWeight: 700, fontFamily: "Manrope, sans-serif" }}>
                            Create a circle
                        </h1>
                    </div>
                </header>
                <div className="flex-1 px-4 pb-10" style={{ marginTop: 16 }}>
                    {formContent()}
                </div>
            </div>
        );
    }

    // ── DESKTOP LAYOUT ───────────────────────────────────────────────────────
    const userName = `${authUser?.first_name || ""} ${authUser?.last_name || ""}`.trim() || "User";
    const avatarUrl = authUser?.avatar_url;
    const email = authUser?.email || "";

    return (
        <div className="min-h-screen flex" style={{ backgroundColor: "#F3F4F6" }}>
            <aside className="flex flex-col" style={{ width: 240, backgroundColor: "#F9FAFB", borderRight: "1px solid #F3F4F6" }}>
                <div className="flex items-center gap-2 px-5 pt-6 pb-8">
                    <svg width={28} height={28} viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg" style={{ borderRadius: 6, flexShrink: 0 }}>
                        <defs><linearGradient id="sb-g" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#4DD4E6" /><stop offset="100%" stopColor="#1B6CF5" /></linearGradient></defs>
                        <rect width="200" height="200" rx="44" fill="url(#sb-g)" />
                        <rect x="78" y="8" width="44" height="22" rx="8" fill="#3BBBD0" />
                        <circle cx="100" cy="95" r="38" fill="#FFF" />
                        <circle cx="106" cy="90" r="22" fill="#0F1B4D" />
                        <circle cx="114" cy="82" r="5" fill="#FFF" />
                        <path d="M72 140 Q100 164 128 140" stroke="#0F1B4D" strokeWidth="6" fill="none" strokeLinecap="round" />
                    </svg>
                    <span style={{ color: "#111827", fontSize: 18, fontWeight: 600, fontFamily: "Manrope, sans-serif" }}>My Buddy</span>
                </div>
                <nav className="flex-1 px-3">
                    {NAV_ITEMS.map((item) => (
                        <button key={item.key} onClick={() => onNavClick(item.key)} className="w-full flex items-center gap-3 px-4 py-3 mb-1 transition-all" style={{ backgroundColor: "transparent", color: "#6B7280", borderRadius: 10, fontSize: 14, fontWeight: 500, fontFamily: "DM Sans, sans-serif" }}>
                            <item.icon className="w-5 h-5" />
                            {item.label}
                        </button>
                    ))}
                </nav>
                <div className="p-4 border-t" style={{ borderColor: "#F3F4F6" }}>
                    <div className="flex items-center gap-3">
                        <div className="flex items-center justify-center overflow-hidden flex-shrink-0" style={{ width: 40, height: 40, borderRadius: 999, backgroundColor: avatarUrl ? "transparent" : PRIMARY, color: "#FFFFFF", fontWeight: 700, fontSize: 14, fontFamily: "Manrope, sans-serif" }}>
                            {avatarUrl ? <img src={avatarUrl} alt={userName} className="w-full h-full object-cover" /> : (userName || "U").charAt(0).toUpperCase()}
                        </div>
                        <div className="min-w-0">
                            <p className="truncate" style={{ color: "#111827", fontSize: 14, fontWeight: 600, fontFamily: "Manrope, sans-serif" }}>{userName}</p>
                            {email ? <p className="truncate" style={{ color: "#6B7280", fontSize: 12, fontFamily: "DM Sans, sans-serif" }}>{email}</p> : null}
                        </div>
                    </div>
                </div>
            </aside>

            <main className="flex-1 overflow-y-auto">
                <div className="flex items-center gap-3" style={{ padding: "24px 40px", borderBottom: "1px solid #E5E7EB", backgroundColor: "#FFFFFF" }}>
                    <button onClick={() => navigate(-1)} className="flex items-center justify-center" style={{ width: 36, height: 36, borderRadius: 10, backgroundColor: "#F3F4F6" }}>
                        <ChevronLeft className="w-5 h-5" style={{ color: "#6B7280" }} />
                    </button>
                    <h1 style={{ color: "#111827", fontSize: 22, fontWeight: 700, fontFamily: "Manrope, sans-serif" }}>Create a circle</h1>
                </div>
                <div style={{ maxWidth: 560, margin: "0 auto", padding: "30px 40px 40px" }}>
                    {formContent()}
                </div>
            </main>
        </div>
    );
}